import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import './LegalSettings.css';

export default function LegalSettings() {
    const [view, setView] = useState('table');
    const [activeDoc, setActiveDoc] = useState('doctor_privacy_policy');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [docsMeta, setDocsMeta] = useState({});

    const docList = [
        { id: 'doctor_privacy_policy', name: 'Doctor Privacy Policy' },
        { id: 'doctor_terms_and_conditions', name: 'Doctor Terms & Conditions' }
    ];

    useEffect(() => {
        if (view === 'table') {
            fetchAllMeta();
        } else {
            fetchDocument(activeDoc);
        }
    }, [view, activeDoc]);

    const fetchAllMeta = async () => {
        setLoading(true);
        try {
            const meta = {};
            for (const d of docList) {
                const docRef = doc(db, 'legal_documents', d.id);
                const snap = await getDoc(docRef);
                if (snap.exists() && snap.data().updatedAt) {
                    const updated = snap.data().updatedAt;
                    meta[d.id] = updated.toDate ? updated.toDate() : new Date(updated);
                } else {
                    meta[d.id] = null;
                }
            }
            setDocsMeta(meta);
        } catch (error) {
            console.error('Error fetching metadata:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDocument = async (docId) => {
        setLoading(true);
        setMessage('');
        try {
            const docRef = doc(db, 'legal_documents', docId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setContent(docSnap.data().content || '');
            } else {
                setContent('');
            }
        } catch (error) {
            console.error('Error fetching document:', error);
            setMessage('Failed to load document.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setMessage('');
        try {
            const docRef = doc(db, 'legal_documents', activeDoc);
            await setDoc(docRef, {
                content,
                updatedAt: new Date()
            }, { merge: true });
            setMessage('Document saved successfully!');
            setTimeout(() => {
                setMessage('');
                setView('table');
            }, 1500);
        } catch (error) {
            console.error('Error saving document:', error);
            setMessage('Failed to save document.');
        } finally {
            setSaving(false);
        }
    };

    if (view === 'table') {
        return (
            <div className="page-container legal-settings">
                <h1>Legal Documents Management</h1>
                
                <div className="legal-table-container">
                    {loading ? (
                        <p style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Loading documents...</p>
                    ) : (
                        <table className="legal-table">
                            <thead>
                                <tr>
                                    <th>Document Name</th>
                                    <th>Status</th>
                                    <th>Last Updated</th>
                                    <th style={{ textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {docList.map(doc => (
                                    <tr key={doc.id}>
                                        <td style={{ fontWeight: '500' }}>{doc.name}</td>
                                        <td><span className="status-badge">Live</span></td>
                                        <td style={{ color: '#666', fontSize: '0.9em' }}>
                                            {docsMeta[doc.id] ? docsMeta[doc.id].toLocaleString() : 'Never updated'}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    setActiveDoc(doc.id);
                                                    setView('editor');
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="page-container legal-settings">
            <button className="back-btn" onClick={() => setView('table')}>
                ← Back to Legal Documents
            </button>
            <h2>Editing: {docList.find(d => d.id === activeDoc)?.name}</h2>

            {message && <div className="message-banner">{message}</div>}

            <div className="legal-editor-container">
                <div className="editor-pane">
                    <h3>Markdown Editor</h3>
                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <textarea 
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="# Enter your markdown text here..."
                            className="markdown-input"
                        />
                    )}
                    <button 
                        className="btn btn-primary save-btn"
                        onClick={handleSave}
                        disabled={loading || saving}
                    >
                        {saving ? 'Saving...' : 'Save Document'}
                    </button>
                </div>
                <div className="preview-pane">
                    <h3>Live Preview</h3>
                    <div className="markdown-preview">
                        {content ? (
                            <ReactMarkdown>{content}</ReactMarkdown>
                        ) : (
                            <p style={{ color: '#aaa', fontStyle: 'italic' }}>Preview will appear here...</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
